import { useEffect, useState } from 'react';
import { Mascot } from '../ui/mascot';
import type { MascotMood, MascotPose } from '../ui/mascot';
import { copy } from './copy';

export type WormMood = MascotMood;
export type WormPose = MascotPose;

export interface WormAction {
  readonly label: string;
  readonly onClick: () => void;
  readonly busy?: boolean;
}

interface WormProps {
  readonly message: string;
  readonly mood?: WormMood;
  readonly pose?: WormPose | undefined;
  readonly action?: WormAction | undefined;
}

const PHONE = '(max-width: 639px)';
const SCROLL_TOLERANCE = 48;

export function Worm({ message, mood = 'idle', pose, action }: WormProps) {
  const [closedMessage, setClosedMessage] = useState<string | null>(null);
  const open = closedMessage !== message;

  useEffect(() => {
    if (!open || action || !window.matchMedia(PHONE).matches) {
      return;
    }
    const start = window.scrollY;
    const tuckAway = () => {
      if (Math.abs(window.scrollY - start) > SCROLL_TOLERANCE) {
        setClosedMessage(message);
      }
    };
    window.addEventListener('scroll', tuckAway, { passive: true });
    return () => {
      window.removeEventListener('scroll', tuckAway);
    };
  }, [open, action, message]);

  return (
    <div className="pointer-events-none fixed right-[max(0.75rem,env(safe-area-inset-right))] bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-40 flex items-end gap-1 sm:right-6 sm:bottom-6">
      {open ? (
        <div
          key={message}
          className="pointer-events-auto mb-12 max-w-64 origin-bottom-right animate-rise rounded-2xl rounded-br-sm bg-white px-4 py-3 shadow-warm ring-1 ring-slate-200 sm:max-w-72"
        >
          <p role="status" aria-live="polite" className="text-base leading-snug text-slate-800">
            {message}
          </p>
          {action ? (
            <button
              type="button"
              disabled={action.busy}
              onClick={action.onClick}
              className="mt-2 min-h-11 rounded-xl bg-emerald-600 px-4 text-base font-bold text-white shadow-sm transition hover:bg-emerald-700 active:scale-95 disabled:opacity-60"
            >
              {action.label}
            </button>
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        aria-label={copy.worm.name}
        aria-expanded={open}
        className="pointer-events-auto rounded-full transition-transform hover:scale-105 active:scale-95"
        onClick={() => {
          setClosedMessage(open ? message : null);
        }}
      >
        <span key={message} className="block origin-bottom motion-safe:animate-nudge">
          <Mascot
            pose={pose ?? (action?.busy ? 'think' : 'idle')}
            mood={mood}
            className="drop-shadow-md"
          />
        </span>
      </button>
    </div>
  );
}
